import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    ArcElement,
    Chart as ChartJS,
    Filler,
    Legend,
    LineElement,
    PointElement,
    RadialLinearScale,
    Tooltip,
} from 'chart.js';
import {Doughnut, Radar} from 'react-chartjs-2';
import {App, Input, Spin, Table, Tooltip as AntTooltip} from 'antd';
import {ReloadOutlined,} from '@ant-design/icons';
import {
    getRadarCoins,
    getRadarOverview,
    RadarCoinData,
    RadarOverview,
    refreshRadarData,
} from '../../services/radarService';
import './RadarPage.css';

// 注册 Chart.js 组件
ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
    ArcElement
);

// 时间周期选项
const TIMEFRAMES = [
    {key: '5m', label: '5分钟'},
    {key: '1H', label: '1小时'},
    {key: '1D', label: '1天'},
];

// 雷达图维度标签
const RADAR_LABELS = [
    '价格动量',
    '交易活跃度',
    '资金费率',
    '多仓比例',
    '社交情绪',
    '技术指标',
];

// 各维度轴在CSS conic-gradient中的角度（conic-gradient 0°=顶部，顺时针60°间隔）
const DIM_ANGLES = [0, 60, 120, 180, 240, 300];

// 币种 Logo 映射 (public/coins/ 下的真实logo)
const COIN_LOGOS: Record<string, string> = {
    BTC: '/coins/btc.webp',
    ETH: '/coins/eth.webp',
    SOL: '/coins/sol.webp',
    BNB: '/coins/bnb.webp',
    XRP: '/coins/xrp.webp',
    ADA: '/coins/ada.webp',
    OKB: '/coins/okb.webp',
    SUI: '/coins/sui.webp',
};

// 币种 Emoji 映射 (无logo时的降级显示)
const COIN_EMOJI: Record<string, string> = {
    BTC: '₿',
    ETH: 'Ξ',
    SOL: '☀️',
    BNB: '🟡',
    XRP: '✕',
    DOGE: '🐶',
    TON: '📱',
    ADA: '🔷',
    AVAX: '❄️',
    PEPE: '🐸',
    SHIB: '🦮',
    LINK: '🔗',
    TRX: '🔺',
    NEAR: '🌐',
};

// 渲染币种图标：有logo用logo，否则不展示
const CoinIcon: React.FC<{ symbol: string; size?: number }> = ({symbol, size = 24}) => {
    const logo = COIN_LOGOS[symbol];
    if (logo) {
        return <img src={logo} alt={symbol} className="radar-coin-logo" style={{width: size, height: size}}/>;
    }
    return null;
};

const RadarPage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const [loading, setLoading] = useState(false);
    const [exchange, setExchange] = useState('OKX');
    const [timeframe, setTimeframe] = useState('1H');
    const [overview, setOverview] = useState<RadarOverview | null>(null);
    const [coins, setCoins] = useState<RadarCoinData[]>([]);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [lastUpdate, setLastUpdate] = useState<string>('');

    // 扫描线DOM引用
    const scanLineRef = useRef<HTMLDivElement>(null);
    // 当前扫描线经过的维度索引（-1表示未在轴线上）
    const [currentDimIndex, setCurrentDimIndex] = useState<number>(-1);

    // 格式化后端返回的最后更新时间（格式：yyyy-MM-dd HH:mm:ss）
    const formatLastUpdateTime = (lastUpdateTime: string): string => {
        if (!lastUpdateTime) return '';
        return lastUpdateTime;
    };

    // 支持的交易所列表
    const EXCHANGES = [
        {key: 'OKX', label: 'OKX', available: true},
        {key: 'Bybit', label: 'Bybit', available: true},
        {key: 'Hyperliquid', label: 'Hyperliquid', available: true},
    ];

    // 加载数据
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [overviewData, coinsData] = await Promise.all([
                getRadarOverview(exchange, timeframe),
                getRadarCoins(exchange, timeframe, 20),
            ]);
            setOverview(overviewData);
            setCoins(coinsData);
            setLastUpdate(formatLastUpdateTime(overviewData.lastUpdateTime));
        } catch (error) {
            console.error('加载雷达数据失败:', error);
            messageApi.error('加载数据失败');
        } finally {
            setLoading(false);
        }
    }, [exchange, timeframe]);

    // 初始加载
    useEffect(() => {
        loadData();
    }, [loadData]);

    // 静默自动刷新（每60秒，先获取数据成功后才更新组件）
    const silentRefresh = useCallback(async () => {
        try {
            const [overviewData, coinsData] = await Promise.all([
                getRadarOverview(exchange, timeframe),
                getRadarCoins(exchange, timeframe, 20),
            ]);
            // 成功获取数据后才更新状态，避免闪烁
            setOverview(overviewData);
            setCoins(coinsData);
            setLastUpdate(formatLastUpdateTime(overviewData.lastUpdateTime));
        } catch {
            // 静默失败，不显示任何提示
        }
    }, [exchange, timeframe]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            silentRefresh();
        }, 60000);
        return () => clearInterval(intervalId);
    }, [silentRefresh]);

    // JS驱动扫描线旋转 + 维度追踪（15秒一圈）
    useEffect(() => {
        const el = scanLineRef.current;
        if (!el) return;
        // 顺时针排序的维度分段（conic-gradient 0°=顶部）：[起始角, 结束角, 维度索引, 上一端点角度]
        const SEGMENTS = [
            {start: 0, end: 60, dimIdx: 0, prevAxis: 0},    // 价格动量
            {start: 60, end: 120, dimIdx: 1, prevAxis: 60},  // 交易活跃度
            {start: 120, end: 180, dimIdx: 2, prevAxis: 120},// 资金费率
            {start: 180, end: 240, dimIdx: 3, prevAxis: 180},// 多仓比例
            {start: 240, end: 300, dimIdx: 4, prevAxis: 240},// 社交情绪
            {start: 300, end: 360, dimIdx: 5, prevAxis: 300},// 技术指标
        ];
        let startTime = performance.now();
        let rafId: number;

        const tick = (now: number) => {
            const elapsed = now - startTime;
            // 每15000ms旋转360度
            const angle = (elapsed / 15000) * 360;
            el.style.transform = `translate(-50%, -50%) rotate(${angle}deg)`;

            // 归一化角度，检测落在哪个分段、已走过上一端点的比例
            const normAngle = ((angle % 360) + 360) % 360;
            let foundDim = -1;
            for (const seg of SEGMENTS) {
                if (normAngle >= seg.start && normAngle < seg.end) {
                    // 计算从上一端点走到当前位置的进度比例
                    let progress = (normAngle - seg.prevAxis) / 60;
                    if (progress < 0) progress = (normAngle + 360 - seg.prevAxis) / 60;
                    // 前2/3区间显示，后1/3隐藏
                    if (progress < 2 / 3) {
                        foundDim = seg.dimIdx;
                    }
                    break;
                }
            }
            setCurrentDimIndex(foundDim);
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, []);

    // 刷新数据
    const handleRefresh = async () => {
        setLoading(true);
        try {
            const data = await refreshRadarData(exchange, timeframe);
            setOverview(data);
            const coinsData = await getRadarCoins(exchange, timeframe, 20);
            setCoins(coinsData);
            setLastUpdate(formatLastUpdateTime(data.lastUpdateTime));
            messageApi.success('数据已刷新');
        } catch (error) {
            messageApi.error('刷新失败');
        } finally {
            setLoading(false);
        }
    };

    // 切换时间周期
    const handleTimeframeChange = (tf: string) => {
        setTimeframe(tf);
    };

    // 雷达图数据
    const radarData = {
        labels: RADAR_LABELS,
        datasets:
            overview?.radarDatasets?.map((ds: {
                label: string;
                data: number[];
                borderColor: string;
                backgroundColor: string
            }) => ({
                label: ds.label,
                data: ds.data,
                borderColor: ds.borderColor,
                backgroundColor: ds.backgroundColor,
                borderWidth: 2,
                pointBackgroundColor: ds.borderColor,
                pointBorderColor: '#000',
                pointRadius: 4,
            })) || [],
    };

    // 雷达图配置
    const radarOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    color: '#aaa',
                    usePointStyle: true,
                    padding: 20,
                    font: {size: 12},
                },
            },
        },
        scales: {
            r: {
                min: 0,
                max: 100,
                ticks: {
                    stepSize: 20,
                    color: '#444',
                    backdropColor: 'transparent',
                },
                grid: {color: '#222'},
                angleLines: {color: '#333'},
                pointLabels: {color: '#aaa', font: {size: 12}},
            },
        },
    };

    // 情绪仪表盘数据
    const gaugeData = {
        datasets: [
            {
                data: [overview?.globalLongRatio || 50, overview?.globalShortRatio || 50],
                backgroundColor: ['var(--pt-bull)', 'var(--pt-bear)'],
                borderColor: '#111',
                borderWidth: 4,
                circumference: 180,
                rotation: -90,
            },
        ],
    };

    const gaugeOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '75%',
        plugins: {legend: {display: false}},
    };

    // 过滤币种
    const filteredCoins = coins.filter(
        (coin) =>
            !searchKeyword ||
            coin.symbol.toLowerCase().includes(searchKeyword.toLowerCase())
    );

    // 表格列定义
    const columns = [
        {
            title: '币种',
            dataIndex: 'symbol',
            key: 'symbol',
            render: (symbol: string) => (
                <div className="radar-coin-symbol">
                    <CoinIcon symbol={symbol} size={24}/>
                    <span>{symbol}</span>
                </div>
            ),
        },
        {
            title: '当前价格',
            dataIndex: 'price',
            key: 'price',
            render: (price: number) => (
                <span className="radar-price">
          {price ? `$${price.toLocaleString(undefined, {maximumFractionDigits: 2})}` : '-'}
        </span>
            ),
        },
        {
            title: '24h 涨跌',
            dataIndex: 'change24h',
            key: 'change24h',
            render: (change: number) => (
                <span className={change >= 0 ? 'radar-positive' : 'radar-negative'}>
          {change >= 0 ? '+' : ''}
                    {change?.toFixed(2) || '-'}%
        </span>
            ),
        },
        {
            title: '多空比',
            key: 'longShortRatio',
            render: (_: unknown, record: RadarCoinData) => (
                <div className="radar-ratio-bar">
                    <span className="radar-long">{record.longRatio?.toFixed(0) || '-'}%</span>
                    <div className="radar-ratio-divider"></div>
                    <span className="radar-short">{record.shortRatio?.toFixed(0) || '-'}%</span>
                </div>
            ),
        },
        {
            title: '雷达信号',
            dataIndex: 'signal',
            key: 'signal',
            render: (signal: string) => {
                const signalMap: Record<string, { emoji: string; label: string; color: string }> = {
                    STRONG_LONG: {emoji: '🟢', label: '强多头', color: 'radar-signal-long'},
                    LONG: {emoji: '🟢', label: '多头', color: 'radar-signal-long'},
                    NEUTRAL: {emoji: '🟡', label: '中性', color: 'radar-signal-neutral'},
                    SHORT: {emoji: '🔴', label: '空头', color: 'radar-signal-short'},
                    STRONG_SHORT: {emoji: '🔴', label: '强空头', color: 'radar-signal-short'},
                };
                const s = signalMap[signal] || {emoji: '⚪', label: signal, color: ''};
                return <span className={`radar-signal ${s.color}`}>{s.emoji} {s.label}</span>;
            },
        },
        {
            title: '强度',
            dataIndex: 'strength',
            key: 'strength',
            render: (strength: number) => (
                <div className="radar-strength">
                    <div className="radar-strength-bar">
                        <div
                            className="radar-strength-fill"
                            style={{width: `${strength || 0}%`}}
                        ></div>
                    </div>
                    <span className="radar-strength-value">{strength || 0}</span>
                </div>
            ),
        },
    ];

    return (
        <div className="radar-page">
            {loading && (
                <div className="radar-loading-overlay">
                    <Spin size="large"/>
                </div>
            )}

            {/* 页面标题 */}
            <div className="radar-header">
                <div className="radar-title-area">
                    <div className="radar-live-badge">
                        <span className="radar-live-dot"></span>
                        雷达正在扫描全市场 {overview?.totalCoinCount || 0} 个币种
                    </div>
                    <h1 className="radar-title">市场雷达</h1>
                    <p className="radar-subtitle">实时捕捉加密市场多空力量变化 · 助力精准交易决策</p>
                </div>

                {/* 交易所切换 */}
                <div className="radar-exchanges">
                    {EXCHANGES.map((ex) => (
                        <button
                            key={ex.key}
                            className={`radar-exchange-btn ${exchange === ex.key ? 'active' : ''} ${!ex.available ? 'disabled' : ''}`}
                            onClick={() => ex.available && setExchange(ex.key)}
                            disabled={!ex.available}
                        >
                            {ex.label}
                        </button>
                    ))}
                </div>

                {/* 时间周期切换 */}
                <div className="radar-timeframes">
                    {TIMEFRAMES.map((tf) => (
                        <button
                            key={tf.key}
                            className={`radar-timeframe-btn ${timeframe === tf.key ? 'active' : ''}`}
                            onClick={() => handleTimeframeChange(tf.key)}
                        >
                            {tf.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* 主内容区域 */}
            <div className="radar-content">
                {/* 左侧：雷达图 */}
                <div className="radar-chart-section">
                    <div className="radar-section-header">
                        <h3>全市场多空雷达图</h3>
                        <span className="radar-badge">多维度分析</span>
                    </div>

                    <div className="radar-chart-container">
                        <Radar data={radarData} options={radarOptions}/>
                        <div className="radar-scan-line" ref={scanLineRef}></div>
                        {/* 维度扫描值面板 */}
                        <div className={`radar-dim-panel${currentDimIndex < 0 ? ' hidden' : ''}`}>
                            {currentDimIndex >= 0 && <div key={currentDimIndex} className="radar-dim-body">
                                <div className="radar-dim-title">{RADAR_LABELS[currentDimIndex]}</div>
                                <div className="radar-dim-list">
                                    {overview?.radarDatasets?.map(ds => (
                                        <div key={ds.label} className="radar-dim-item">
                                            <span className="radar-dim-coin">{ds.label}</span>
                                            <span className="radar-dim-val">{ds.data[currentDimIndex]}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>}
                        </div>
                        <div className="radar-center-value">
                            <div className="radar-center-label">CURRENT</div>
                            <div className="radar-center-main">
                                {overview?.globalLongRatio?.toFixed(0) || 50}% LONG
                            </div>
                            <div className="radar-center-desc">
                                {overview?.marketSentiment === 'BULLISH'
                                    ? '多头力量占优'
                                    : overview?.marketSentiment === 'BEARISH'
                                        ? '空头力量占优'
                                        : '多空势均力敌'}
                            </div>
                        </div>
                    </div>

                    {/* 统计卡片 */}
                    <div className="radar-stats">
                        <div className="radar-stat-card radar-stat-long">
                            <div className="radar-stat-title">多头主导币种</div>
                            <div className="radar-stat-value">{overview?.longDominantCount || 0}</div>
                            <div className="radar-stat-examples">
                                {overview?.topLongCoins?.slice(0, 3).map((c) => c.symbol).join(' · ') || '-'}
                            </div>
                        </div>
                        <div className="radar-stat-card radar-stat-short">
                            <div className="radar-stat-title">空头主导币种</div>
                            <div className="radar-stat-value">{overview?.shortDominantCount || 0}</div>
                            <div className="radar-stat-examples">
                                {overview?.topShortCoins?.slice(0, 3).map((c) => c.symbol).join(' · ') || '-'}
                            </div>
                        </div>
                        <div className="radar-stat-card radar-stat-neutral">
                            <div className="radar-stat-title">中性震荡</div>
                            <div className="radar-stat-value">{overview?.neutralCount || 0}</div>
                            <div className="radar-stat-examples">等待突破信号</div>
                        </div>
                    </div>
                </div>

                {/* 右侧：概览 */}
                <div className="radar-overview-section">
                    {/* 情绪仪表盘 */}
                    <div className="radar-gauge-card">
                        <div className="radar-section-header">
                            <h3>市场整体情绪</h3>
                            <span className={`radar-sentiment-badge ${overview?.marketSentiment?.toLowerCase()}`}>
                {overview?.marketSentiment || 'NEUTRAL'}
              </span>
                        </div>
                        <div className="radar-gauge-container">
                            <Doughnut data={gaugeData} options={gaugeOptions}/>
                            <div className="radar-gauge-center">
                                <div className="radar-gauge-value">
                                    {overview?.globalLongRatio?.toFixed(0) || 50}
                                </div>
                                <div className="radar-gauge-label">多头占比</div>
                            </div>
                        </div>
                    </div>

                    {/* 顶级多头/空头卡片 */}
                    <div className="radar-top-cards">
                        {/* 多头卡片 */}
                        <div className="radar-top-card radar-top-long">
                            <div className="radar-top-header">🔥 顶级多头</div>
                            <div className="radar-top-list">
                                {overview?.topLongCoins?.map((coin, idx) => (
                                    <div key={idx} className="radar-top-item">
                                        <div className="radar-top-coin">
                                            <CoinIcon symbol={coin.symbol} size={20}/>
                                            <span>{coin.symbol}</span>
                                        </div>
                                        <div className="radar-top-data">
                                            <div className="radar-top-ratio">{coin.ratio?.toFixed(0)}%</div>
                                            <div className="radar-top-change">
                                                {coin.change24h >= 0 ? '+' : ''}
                                                {coin.change24h?.toFixed(1)}% 24h
                                            </div>
                                        </div>
                                    </div>
                                )) || <div className="radar-top-empty">暂无数据</div>}
                            </div>
                        </div>

                        {/* 空头卡片 */}
                        <div className="radar-top-card radar-top-short">
                            <div className="radar-top-header">❄️ 顶级空头</div>
                            <div className="radar-top-list">
                                {overview?.topShortCoins?.map((coin, idx) => (
                                    <div key={idx} className="radar-top-item">
                                        <div className="radar-top-coin">
                                            <CoinIcon symbol={coin.symbol} size={20}/>
                                            <span>{coin.symbol}</span>
                                        </div>
                                        <div className="radar-top-data">
                                            <div className="radar-top-ratio">{coin.ratio?.toFixed(0)}%</div>
                                            <div className="radar-top-change">
                                                {coin.change24h >= 0 ? '+' : ''}
                                                {coin.change24h?.toFixed(1)}% 24h
                                            </div>
                                        </div>
                                    </div>
                                )) || <div className="radar-top-empty">暂无数据</div>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 下方：币种列表 */}
                <div className="radar-table-section">
                    <div className="radar-section-header">
                        <h3>全市场多空信号榜</h3>
                        <div className="radar-table-controls">
                            <Input
                                placeholder="搜索币种..."
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)}
                                className="radar-search-input"
                            />
                            <AntTooltip title="刷新数据">
                                <button className="radar-refresh-btn" onClick={handleRefresh}>
                                    <ReloadOutlined spin={loading}/>
                                </button>
                            </AntTooltip>
                        </div>
                    </div>
                    <div className="radar-last-update-row">
            <span className="radar-last-update">
              最后更新：{lastUpdate}
            </span>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={filteredCoins}
                        rowKey="symbol"
                        pagination={false}
                        className="radar-table"
                    />
                </div>
            </div>
        </div>
    );
};

export default RadarPage;
